import { normalizePlanar } from "../core/math.ts";

const MOVEMENT_CODES = Object.freeze({
  forward: ["KeyW", "ArrowUp"],
  backward: ["KeyS", "ArrowDown"],
  left: ["KeyA", "ArrowLeft"],
  right: ["KeyD", "ArrowRight"],
});

const EDGE_ACTION_CODES = new Set([
  "Space",
  "KeyE",
  "KeyF",
  "KeyG",
  "KeyR",
  "KeyJ",
  "KeyK",
  "KeyZ",
  "KeyC",
  "Backspace",
]);

export interface KeyboardMapping {
  moveX: number;
  moveZ: number;
  jumpHeld: boolean;
  descendHeld: boolean;
  sprintHeld: boolean;
  interactHeld: boolean;
  brakeHeld: boolean;
  reverseHeld: boolean;
  handbrakeHeld: boolean;
  hornHeld: boolean;
  lookBehindHeld: boolean;
}

export interface KeyboardSample extends KeyboardMapping {
  jumpPressed: boolean;
  interactPressed: boolean;
  toggleFlightPressed: boolean;
  cycleSeatPressed: boolean;
  toggleHeadlightsPressed: boolean;
  toggleBeaconPressed: boolean;
  toggleSirenPressed: boolean;
  toggleLeftIndicatorPressed: boolean;
  toggleRightIndicatorPressed: boolean;
  recoverVehiclePressed: boolean;
}

function hasAnyCode(
  pressedCodes: ReadonlySet<string>,
  codes: readonly string[],
): boolean {
  return codes.some((code) => pressedCodes.has(code));
}

function isEditableTarget(target: EventTarget | null): boolean {
  if (typeof HTMLElement === "undefined") return false;
  return target instanceof HTMLInputElement ||
    target instanceof HTMLTextAreaElement ||
    target instanceof HTMLSelectElement ||
    (target instanceof HTMLElement && target.isContentEditable);
}

/** Pure keyboard-to-control mapping used by both runtime input and unit tests. */
export function mapKeyboardCodes(
  pressedCodes: ReadonlySet<string>,
): KeyboardMapping {
  const rawX =
    Number(hasAnyCode(pressedCodes, MOVEMENT_CODES.right)) -
    Number(hasAnyCode(pressedCodes, MOVEMENT_CODES.left));
  const rawZ =
    Number(hasAnyCode(pressedCodes, MOVEMENT_CODES.forward)) -
    Number(hasAnyCode(pressedCodes, MOVEMENT_CODES.backward));
  const movement = normalizePlanar(rawX, rawZ);

  return {
    moveX: movement.x,
    moveZ: movement.z,
    jumpHeld: pressedCodes.has("Space"),
    descendHeld:
      pressedCodes.has("ControlLeft") ||
      pressedCodes.has("ControlRight"),
    sprintHeld:
      pressedCodes.has("ShiftLeft") || pressedCodes.has("ShiftRight"),
    interactHeld: pressedCodes.has("KeyE"),
    brakeHeld: hasAnyCode(pressedCodes, MOVEMENT_CODES.backward),
    reverseHeld: pressedCodes.has("KeyX"),
    handbrakeHeld: pressedCodes.has("Space"),
    hornHeld: pressedCodes.has("KeyH"),
    lookBehindHeld: pressedCodes.has("KeyV"),
  };
}

export class KeyboardInputAdapter {
  readonly #pressedCodes = new Set<string>();
  readonly #pressedEdges = new Set<string>();
  readonly #target: Window;
  #attached = false;

  constructor(target: Window) {
    this.#target = target;
  }

  readonly #onKeyDown = (event: KeyboardEvent): void => {
    if (isEditableTarget(event.target)) return;
    if (event.code === "Backspace") {
      event.preventDefault();
    }
    if (!this.#pressedCodes.has(event.code) && EDGE_ACTION_CODES.has(event.code)) {
      this.#pressedEdges.add(event.code);
    }
    this.#pressedCodes.add(event.code);
  };

  readonly #onKeyUp = (event: KeyboardEvent): void => {
    // Always release held game controls, even if focus moved into chat after
    // keydown. Otherwise opening chat while walking can leave movement stuck.
    this.#pressedCodes.delete(event.code);
  };

  readonly #onBlur = (): void => {
    this.#pressedCodes.clear();
    this.#pressedEdges.clear();
  };

  attach(): void {
    if (this.#attached) {
      return;
    }
    this.#target.addEventListener("keydown", this.#onKeyDown);
    this.#target.addEventListener("keyup", this.#onKeyUp);
    this.#target.addEventListener("blur", this.#onBlur);
    this.#attached = true;
  }

  detach(): void {
    if (!this.#attached) {
      return;
    }
    this.#target.removeEventListener("keydown", this.#onKeyDown);
    this.#target.removeEventListener("keyup", this.#onKeyUp);
    this.#target.removeEventListener("blur", this.#onBlur);
    this.#pressedCodes.clear();
    this.#pressedEdges.clear();
    this.#attached = false;
  }

  diagnostics(): Readonly<{
    attached: boolean;
    pressedCodes: readonly string[];
  }> {
    return {
      attached: this.#attached,
      pressedCodes: [...this.#pressedCodes].sort(),
    };
  }

  sample(): KeyboardSample {
    const mapping = mapKeyboardCodes(this.#pressedCodes);
    const sample = {
      ...mapping,
      jumpPressed: this.#pressedEdges.has("Space"),
      interactPressed: this.#pressedEdges.has("KeyE"),
      toggleFlightPressed: this.#pressedEdges.has("KeyF"),
      cycleSeatPressed: this.#pressedEdges.has("KeyR"),
      toggleHeadlightsPressed: this.#pressedEdges.has("KeyG"),
      toggleBeaconPressed: this.#pressedEdges.has("KeyJ"),
      toggleSirenPressed: this.#pressedEdges.has("KeyK"),
      toggleLeftIndicatorPressed: this.#pressedEdges.has("KeyZ"),
      toggleRightIndicatorPressed: this.#pressedEdges.has("KeyC"),
      recoverVehiclePressed: this.#pressedEdges.has("Backspace"),
    };

    this.#pressedEdges.delete("Space");
    this.#pressedEdges.delete("KeyE");
    this.#pressedEdges.delete("KeyF");
    this.#pressedEdges.delete("KeyR");
    this.#pressedEdges.delete("KeyG");
    this.#pressedEdges.delete("KeyJ");
    this.#pressedEdges.delete("KeyK");
    this.#pressedEdges.delete("KeyZ");
    this.#pressedEdges.delete("KeyC");
    this.#pressedEdges.delete("Backspace");
    return sample;
  }
}
